// =============================================================================
// Optim TURPE V2 — LE REGISTRE DES ENTRÉES et les SURCHARGES (DEV-1913, A18).
//
// Principe posé par Mathieu : « tout ce qui est donné dans l'étude, à part les
// historiques de courbe de charge, doit pouvoir être surchargé », chaque surcharge
// étant tracée, montrée au résultat et reprise au rapport.
//
// Ce que le client doit vivre : pour chaque valeur qui a servi au calcul, il lit
// d'où elle vient (Enedis, notre défaut, sa propre correction), quand elle a été
// relevée, et s'il peut la corriger. Une valeur que nous avons choisie faute de
// mieux ne se cache pas derrière un résultat.
//
// Le repli : quand le moteur ne sert pas `restitution.entrees`, la page ne
// fabrique pas un registre depuis `inputs` — elle dit que la provenance des
// valeurs n'est pas encore servie, et les champs restent corrigeables dans
// « Affiner l'étude » comme aujourd'hui.
// =============================================================================

import {
    LIBELLE_CHAMP,
    libelleChamp,
    LIBELLE_OPTION_TARIFAIRE,
    LIBELLE_PROVENANCE,
    LIBELLE_PROVENANCE_INCONNUE,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2EntreeRegistre,
    OptimTurpeV2Etude,
    OptimTurpeV2Provenance,
    OptimTurpeV2Reserve,
} from "@modules/courtage/types/optim-turpe-v2"

/** Le chemin de l'entrée « heures creuses », cité par la réserve dédiée (§3.4). */
export const CHAMP_HEURES_CREUSES = "comptage.plages_horaires.heures_creuses"

export type EntreeLue = {
    champ: string
    libelle: string
    /** Valeur RETENUE par le calcul, telle quelle. Jamais reformatée en nombre. */
    valeur: unknown
    unite: string | null
    provenance: OptimTurpeV2Provenance | null
    /**
     * La provenance que la colonne « Source » dit (partie 4 de la vague du
     * 14/10/2026). Égale à `provenance`, sauf pour une valeur `derive` : le
     * moteur ne dérive que le segment, et le dérive de la formule tarifaire
     * d'acheminement (courtage `registre_entrees.py`, DEV-2176) ; sa source est
     * donc celle de la formule du même registre (Enedis, votre saisie ou la
     * valeur par défaut). `null` quand cette source n'est pas servie.
     */
    provenanceSource: OptimTurpeV2Provenance | null
    provenanceLibelle: string | null
    releveLe: string | null
    /** Surcharge tracée : qui, quand, et ce qu'elle a remplacé. */
    surcharge: OptimTurpeV2EntreeRegistre["surcharge"]
    estSurchargee: boolean
    surchargeable: boolean
    peseSurLeResultat: boolean
    statut: "retenue" | "ecartee" | "absente"
    /** Vrai pour une valeur que nous avons choisie faute d'être servie (A18). */
    estAuDefaut: boolean
}

/**
 * La marge de sécurité est un réglage du produit (affiché « Prudence »), pas
 * une hypothèse de contrat : prise par défaut, elle ne se confirme pas.
 */
export const CHAMP_MARGE_SECURITE = "optimisation.marge_securite_pct"

/** Le chemin de la date du barème TURPE. */
export const CHAMP_DATE_BAREME =
    "structure_tarifaire.offre_tarifaire.turpe.date"

/**
 * La valeur RETENUE d'une entrée. Une date de barème vide n'est pas une
 * absence : le moteur applique alors le barème du jour du calcul, et la date
 * qu'il a retenue est servie dans `calcul.bareme.date_retenue` (reprise du
 * pilote du 25/09). C'est elle que la ligne montre.
 */
function valeurRetenue(
    etude: OptimTurpeV2Etude,
    entree: OptimTurpeV2EntreeRegistre
): unknown {
    const valeur = entree.valeur ?? null
    if (valeur === null && entree.champ === CHAMP_DATE_BAREME) {
        return etude.calcul?.bareme?.date_retenue ?? null
    }
    return valeur
}

/** Le chemin de la formule tarifaire d'acheminement dans le registre. */
const CHAMP_FORMULE = "structure_tarifaire.formule_tarifaire_acheminement.fta"

/**
 * La source de la formule tarifaire d'acheminement du registre, dont le moteur
 * dérive le segment : une saisie se prouve par sa provenance ou par sa trace.
 * `null` quand la formule n'est pas dans le registre, ou qu'elle est elle-même
 * dérivée (ce que le moteur ne fait pas).
 */
function provenanceDeLaFormule(
    entrees: OptimTurpeV2EntreeRegistre[]
): OptimTurpeV2Provenance | null {
    const formule = entrees.find((e) => e?.champ === CHAMP_FORMULE)
    if (!formule) return null
    if (formule.surcharge) return "surcharge_client"
    const provenance = formule.provenance ?? null
    return provenance === "derive" ? null : provenance
}

/**
 * Le registre servi par le moteur, mis en forme pour l'écran.
 *
 * Rend `null` quand le bloc n'est pas servi — c'est une absence à dire, pas un
 * registre vide à afficher.
 */
export function lireRegistre(etude: OptimTurpeV2Etude): EntreeLue[] | null {
    const entrees = etude.entrees
    if (!Array.isArray(entrees) || entrees.length === 0) return null
    const sourceDeLaFormule = provenanceDeLaFormule(entrees)
    return entrees
        .filter((entree) => typeof entree?.champ === "string")
        .map((entree) => {
            const provenance = entree.provenance ?? null
            const surcharge = entree.surcharge ?? null
            const provenanceSource =
                provenance === "derive" ? sourceDeLaFormule : provenance
            return {
                champ: String(entree.champ),
                // Le nom de la page d'abord (relu au texte-ok), puis celui que
                // le moteur sert pour un champ que la page ne nomme pas encore.
                libelle:
                    (entree.champ ? LIBELLE_CHAMP[entree.champ] : undefined) ??
                    entree.libelle ??
                    libelleChamp(entree.champ),
                valeur: valeurRetenue(etude, entree),
                unite: entree.unite ?? null,
                provenance,
                provenanceSource,
                // Jamais le code brut d'une provenance devant le client
                // (05/10/2026) : une provenance inconnue est « non précisée »,
                // comme une valeur dérivée dont la source n'est pas servie.
                provenanceLibelle: provenance
                    ? provenanceSource
                        ? (LIBELLE_PROVENANCE[provenanceSource] ??
                          LIBELLE_PROVENANCE_INCONNUE)
                        : LIBELLE_PROVENANCE_INCONNUE
                    : null,
                releveLe: entree.releve_le ?? null,
                surcharge,
                // Une surcharge se prouve par sa TRACE ou par sa provenance ; les deux
                // existent parce qu'un rejeu reprend la surcharge de l'étude précédente
                // en conservant l'auteur et la date d'origine (§3.2).
                estSurchargee:
                    provenance === "surcharge_client" || surcharge !== null,
                surchargeable: entree.surchargeable !== false,
                peseSurLeResultat: entree.pese_sur_le_resultat === true,
                statut: entree.statut ?? "retenue",
                // Revue du 08/10/2026 (R5) : la date du barème n'est jamais « à
                // confirmer » par le client, le moteur applique le barème en
                // vigueur ; elle reste dite dans le registre (« Valeur par défaut
                // de l'étude »), comme la marge de sécurité.
                estAuDefaut:
                    provenance === "defaut_moteur" &&
                    entree.pese_sur_le_resultat === true &&
                    entree.champ !== CHAMP_MARGE_SECURITE &&
                    entree.champ !== CHAMP_DATE_BAREME,
            }
        })
}

/**
 * Les unités du registre qui ne s'écrivent pas à côté d'une valeur : ce sont des
 * TYPES de valeur (courtage#249 `registre_entrees`), pas des unités physiques.
 */
const UNITES_DE_TYPE = new Set(["fraction", "booleen", "plages", "date"])

/** Une heure servie `22:00:00` s'écrit `22:00`. */
function heure(valeur: unknown): string {
    return String(valeur ?? "").slice(0, 5)
}

/**
 * La valeur d'une entrée, écrite à la française et SANS jamais être reformatée
 * en un autre nombre : une fraction se lit en %, une date `JJ/MM/AAAA`, des
 * plages `22:00 → 06:00`, un booléen « oui » ou « non ». L'unité physique
 * (kVA, kW) suit la valeur ; un type de valeur ne s'écrit jamais.
 */
export function formaterValeurEntree(
    valeur: unknown,
    unite: string | null,
    formatNombre: (n: number, decimales?: number) => string | null
): string {
    if (valeur === null || valeur === undefined || valeur === "") {
        return "non renseignée"
    }
    const suffixe = unite && !UNITES_DE_TYPE.has(unite) ? `\u00a0${unite}` : ""
    if (typeof valeur === "boolean") return valeur ? "oui" : "non"
    if (unite === "fraction" && typeof valeur === "number") {
        return `${formatNombre(valeur * 100, 1) ?? "—"}\u00a0%`
    }
    if (unite === "date" && typeof valeur === "string") {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(valeur)
        return m ? `${m[3]}/${m[2]}/${m[1]}` : valeur
    }
    if (Array.isArray(valeur)) {
        return valeur
            .map((item) => {
                if (item && typeof item === "object") {
                    const plage = item as Record<string, unknown>
                    if ("time_from" in plage || "time_to" in plage) {
                        return `${heure(plage.time_from)} → ${heure(plage.time_to)}`
                    }
                    return Object.values(plage).join(" → ")
                }
                return String(item)
            })
            .join(", ")
    }
    if (typeof valeur === "object") {
        // Une classe sans valeur ne s'imprime pas (recette du 28/09 : « PM
        // null » dans le dénivelé d'une FTA sans pointe). Seules les classes
        // qui portent une valeur sont écrites ; aucune, et l'entrée est dite
        // non renseignée, jamais une suite de « null ».
        const portees = Object.entries(
            valeur as Record<string, unknown>
        ).filter(([, v]) => v !== null && v !== undefined && v !== "")
        if (portees.length === 0) return "non renseignée"
        return `${portees
            .map(([cle, v]) =>
                typeof v === "number"
                    ? `${cle} ${formatNombre(v) ?? String(v)}`
                    : `${cle} ${String(v)}`
            )
            .join(" · ")}${suffixe}`
    }
    if (typeof valeur === "number") {
        return `${formatNombre(valeur) ?? "—"}${suffixe}`
    }
    return `${String(valeur)}${suffixe}`
}

/** Les seules entrées qui ont un effet sur cette étude, dans l'ordre servi. */
export function entreesQuiPesent(entrees: EntreeLue[] | null): EntreeLue[] {
    return (entrees ?? []).filter((entree) => entree.peseSurLeResultat)
}

/** Les surcharges du client, à montrer au résultat et à reprendre au rapport (A18). */
export function surchargesDuClient(entrees: EntreeLue[] | null): EntreeLue[] {
    return (entrees ?? []).filter((entree) => entree.estSurchargee)
}

/**
 * Les réserves qui portent sur UNE entrée, indexées par champ, pour être rendues
 * sur la ligne concernée du registre plutôt qu'en liste séparée.
 *
 * Une réserve `portee: "entree"` sans `champ` n'est pas rattachable : elle reste
 * dans la liste générale, elle n'est pas perdue ici.
 */
export function reservesParChamp(
    reserves: OptimTurpeV2Reserve[] | null | undefined
): Record<string, OptimTurpeV2Reserve[]> {
    const parChamp: Record<string, OptimTurpeV2Reserve[]> = {}
    for (const reserve of reserves ?? []) {
        if (reserve.portee !== "entree" || !reserve.champ) continue
        parChamp[reserve.champ] = [...(parChamp[reserve.champ] ?? []), reserve]
    }
    return parChamp
}

/**
 * Les plages d'heures creuses PROPOSÉES quand Enedis n'en publie pas (§3.4, A18).
 *
 * Le fait et la proposition viennent tous deux de la réserve
 * `heures-creuses-non-publiees` servie par le moteur — la page n'invente ni le
 * fait ni les plages. Rend `null` quand la réserve n'est pas posée.
 */
export function heuresCreusesProposees(
    reserves: OptimTurpeV2Reserve[] | null | undefined
): {
    label: string
    plages: Array<{ time_from?: string; time_to?: string }>
} | null {
    const reserve = (reserves ?? []).find(
        (r) => r.code === "heures-creuses-non-publiees"
    )
    if (!reserve) return null
    const proposees = reserve.valeurs?.plages_proposees
    return {
        label: reserve.label,
        plages: Array.isArray(proposees)
            ? (proposees as Array<{ time_from?: string; time_to?: string }>)
            : [],
    }
}

/**
 * La valeur retenue d'une entrée au défaut, écrite pour un client : des plages
 * « 22:00 à 06:00 », un nombre tel quel, une valeur d'énumération par son
 * libellé (« pointe fixe » pour `HS_PTE_Fixe`). Une valeur d'énumération sans
 * libellé connu rend `null` : jamais de valeur brute devant le client (lexique
 * client v0.1, 05/10/2026). `null` sans valeur.
 */
export function texteValeurRetenue(valeur: unknown): string | null {
    if (valeur === null || valeur === undefined || valeur === "") return null
    if (Array.isArray(valeur)) {
        const plages = valeur
            .filter(
                (p): p is { time_from?: unknown; time_to?: unknown } =>
                    Boolean(p) && typeof p === "object"
            )
            .filter((p) => p.time_from && p.time_to)
            .sort((a, b) =>
                String(a.time_from).localeCompare(String(b.time_from))
            )
            .map((p) => `${heure(p.time_from)} à ${heure(p.time_to)}`)
        return plages.length > 0 ? plages.join(" et ") : null
    }
    if (typeof valeur === "number")
        return Number.isFinite(valeur) ? String(valeur) : null
    if (typeof valeur !== "string") return null
    if (/^\d+(?:[.,]\d+)?$/.test(valeur.trim())) return valeur.trim()
    return LIBELLE_OPTION_TARIFAIRE[valeur] ?? null
}

/**
 * Le libellé d'une réserve d'entrée. Une réserve `entree-au-defaut` NOMME la
 * valeur retenue (courtage#256, DEV-2186) : « Valeur par défaut retenue :
 * 22:00 à 06:00 ». Un libellé servi qui la nomme déjà (« … prise par défaut :
 * pointe fixe. ») n'est pas complété, et une valeur sans libellé client non
 * plus.
 */
export function libelleReserveEntree(reserve: OptimTurpeV2Reserve): string {
    const label = reserve.label ?? ""
    if (reserve.code !== "entree-au-defaut") return label
    if (/valeur par défaut retenue\s*:/i.test(label)) return label
    const texte = texteValeurRetenue(reserve.valeurs?.valeur_retenue)
    if (!texte) return label
    // W27 (14/10/2026) : l'entrée se nomme « Pointe fixe ou pointe mobile » ;
    // ce NOM ne dit pas la valeur retenue, il ne compte pas.
    const sansLeNom = label
        .toLowerCase()
        .replace(/pointe fixe ou pointe mobile/g, "")
    if (sansLeNom.includes(texte.toLowerCase())) return label
    const base = label.trim().replace(/\.$/, "")
    return `${base}. Valeur par défaut retenue : ${texte}.`
}
